import { useEffect, useMemo, useState } from 'react';
import {
  createMerchantGroupBodySchema,
  updateMerchantGroupBodySchema,
  type CreateMerchantGroupBodyType,
  type UpdateMerchantGroupBodyType,
} from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import { useMerchantGroupDetail } from '@/hooks-v2/aggregator';
import type { MerchantMember } from '@/types/common.types';
import CardBackgroundControl from './CardBackgroundControl';
import CardBalanceForm from './CardBalanceForm';
import type { MerchantGroupCard } from '@/types/merchant-groups.types';

interface ManageGroupsPanelProps {
  merchantId: number;
  members: MerchantMember[];
  groups: MerchantGroupCard[];
  isLoading: boolean;
  error: Error | null;
  onRefresh: () => void;
  onCreate: (data: CreateMerchantGroupBodyType) => Promise<unknown>;
  onUpdate: (cardId: string, data: UpdateMerchantGroupBodyType) => Promise<unknown>;
  onAssign: (cardId: string, userId: string) => Promise<unknown>;
  onRemove: (cardId: string, userId: string) => Promise<unknown>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'request-failed';
}

export default function ManageGroupsPanel({
  merchantId,
  members,
  groups,
  isLoading,
  error,
  onRefresh,
  onCreate,
  onUpdate,
  onAssign,
  onRemove,
}: ManageGroupsPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(groups[0]?.card_id ?? null);
  const [groupName, setGroupName] = useState('');
  const [memberId, setMemberId] = useState('');
  const [assignmentUserId, setAssignmentUserId] = useState('');
  const [editName, setEditName] = useState('');
  const [editMemberId, setEditMemberId] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const detail = useMerchantGroupDetail(merchantId, selectedCardId, selectedCardId !== null);

  useEffect(() => {
    if (selectedCardId === null && groups[0] !== undefined) setSelectedCardId(groups[0].card_id);
    if (selectedCardId !== null && !groups.some(group => group.card_id === selectedCardId)) {
      setSelectedCardId(groups[0]?.card_id ?? null);
    }
  }, [groups, selectedCardId]);

  useEffect(() => {
    if (detail.group !== null) {
      setEditName(detail.group.group_name);
      setEditMemberId(detail.group.member_id);
    }
  }, [detail.group]);

  const assignedUserIds = useMemo(
    () => new Set((detail.group?.members ?? []).map(member => member.user_id)),
    [detail.group?.members],
  );
  const availableMembers = members.filter(member => member.status === 'active' && !assignedUserIds.has(member.userId));

  const run = async (action: () => Promise<unknown>, success: string): Promise<void> => {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      setFeedback(success);
      void detail.refetch();
      onRefresh();
    } catch (actionError: unknown) {
      setFeedback(`${t('merchant.memberList.groups.actionFailed')}: ${errorMessage(actionError)}`);
    } finally {
      setBusy(false);
    }
  };

  const create = (): void => {
    const parsed = createMerchantGroupBodySchema.safeParse({ group_name: groupName, member_id: memberId });
    if (!parsed.success) {
      setFeedback(t('merchant.memberList.groups.invalid')); return;
    }
    void run(async () => {
      const created = await onCreate(parsed.data);
      if (created !== null && typeof created === 'object' && 'card_id' in created) {
        const cardId = created.card_id;
        if (typeof cardId === 'string') setSelectedCardId(cardId);
      }
      setGroupName(''); setMemberId('');
    }, t('merchant.memberList.groups.created'));
  };

  const update = (): void => {
    if (detail.group === null) return;
    const parsed = updateMerchantGroupBodySchema.safeParse({
      group_name: editName,
      member_id: editMemberId,
      expected_revision: detail.group.revision,
    });
    if (!parsed.success) {
      setFeedback(t('merchant.memberList.groups.invalid')); return;
    }
    void run(() => onUpdate(detail.group?.card_id ?? '', parsed.data), t('merchant.memberList.groups.updated'));
  };

  return (
    <section className="space-y-4 rounded-xl border bg-white p-4 sm:p-6" aria-labelledby="manage-groups-heading">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="manage-groups-heading" className="text-lg font-semibold text-gray-900">{t('merchant.memberList.groups.title')}</h2>
          <p className="mt-1 text-sm text-gray-600">{t('merchant.memberList.groups.description')}</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={isLoading || busy} className="rounded border px-3 py-2 text-sm">{t('merchant.memberList.groups.refresh')}</button>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div>
          <h3 className="mb-2 font-medium text-gray-900">{t('merchant.memberList.groups.listTitle')}</h3>
          {isLoading && <p role="status">{t('merchant.memberList.groups.loading')}</p>}
          {error !== null && <div><p role="alert">{t('merchant.memberList.groups.loadFailed')}</p><button type="button" onClick={onRefresh}>{t('merchant.memberList.groups.retry')}</button></div>}
          {!isLoading && error === null && groups.length === 0 && <p className="text-sm text-gray-600">{t('merchant.memberList.groups.empty')}</p>}
          <div className="space-y-2">
            {groups.map(group => (
              <button key={group.card_id} type="button" onClick={() => setSelectedCardId(group.card_id)} className={`block w-full rounded border p-3 text-left ${selectedCardId === group.card_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <span className="block font-medium text-gray-900">{group.group_name}</span>
                <span className="block font-mono text-sm text-gray-600">{group.member_id}</span>
                <span className="block text-xs text-gray-500">{t('merchant.memberList.groups.memberCount', { count: group.member_count })}</span>
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-2 border-t pt-4">
            <h3 className="font-medium text-gray-900">{t('merchant.memberList.groups.createTitle')}</h3>
            <label htmlFor="create-group-name" className="block text-sm font-medium text-gray-700">{t('merchant.memberList.groups.nameLabel')}</label>
            <input id="create-group-name" value={groupName} onChange={event => setGroupName(event.target.value)} placeholder={t('merchant.memberList.groups.namePlaceholder')} className="w-full rounded border px-3 py-2" />
            <label htmlFor="create-group-member-id" className="block text-sm font-medium text-gray-700">{t('merchant.memberList.groups.memberIdLabel')}</label>
            <input id="create-group-member-id" value={memberId} onChange={event => setMemberId(event.target.value)} placeholder={t('merchant.memberList.groups.memberIdPlaceholder')} className="w-full rounded border px-3 py-2" />
            <button type="button" disabled={busy} onClick={create} className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{t('merchant.memberList.groups.create')}</button>
          </div>
        </div>
        <div>
          {detail.isLoading && <p role="status">{t('merchant.memberList.groups.loadingDetail')}</p>}
          {detail.error !== null && <p role="alert">{t('merchant.memberList.groups.loadFailed')}</p>}
          {detail.group !== null && (
            <div className="space-y-4">
              <div className="rounded border p-3">
                <h3 className="font-medium text-gray-900">{t('merchant.memberList.groups.detailTitle')}</h3>
                <label htmlFor="edit-group-name" className="mt-2 block text-sm font-medium text-gray-700">{t('merchant.memberList.groups.editNameLabel')}</label>
                <input id="edit-group-name" value={editName} onChange={event => setEditName(event.target.value)} disabled={busy} className="mt-1 w-full rounded border px-3 py-2" />
                <label htmlFor="edit-group-member-id" className="mt-2 block text-sm font-medium text-gray-700">{t('merchant.memberList.groups.editMemberIdLabel')}</label>
                <input id="edit-group-member-id" value={editMemberId} onChange={event => setEditMemberId(event.target.value)} disabled={busy} className="mt-2 w-full rounded border px-3 py-2 font-mono" />
                <p className="mt-2 text-xs text-gray-500">{t('merchant.memberList.groups.revision', { revision: detail.group.revision })}</p>
                <button type="button" disabled={busy} onClick={update} className="mt-2 rounded border px-3 py-2 text-sm">{t('merchant.memberList.groups.save')}</button>
                <CardBalanceForm key={detail.group.card_id} merchantId={merchantId} cardId={detail.group.card_id} idPrefix="group" />
                <CardBackgroundControl
                  key={`${detail.group.card_id}-${detail.group.revision}`}
                  merchantId={merchantId}
                  cardId={detail.group.card_id}
                  revision={detail.group.revision}
                  imageUrl={detail.group.background_image_url}
                  idPrefix="group"
                  onChanged={() => { void detail.refetch(); onRefresh(); }}
                />
              </div>
              <div className="rounded border p-3">
                <h3 className="font-medium text-gray-900">{t('merchant.memberList.groups.assignedTitle')}</h3>
                {detail.group.members.length === 0 && <p className="mt-2 text-sm text-gray-600">{t('merchant.memberList.groups.noAssigned')}</p>}
                <div className="mt-2 space-y-2">
                  {detail.group.members.map(member => (
                    <div key={member.user_id} className="flex items-center justify-between gap-2 rounded bg-gray-50 p-2 text-sm">
                      <span className="flex flex-col"><span>{member.display_name ?? t('merchant.memberList.groups.unknownMember')}</span><span>{member.phone_masked}</span></span>
                      <button type="button" disabled={busy} onClick={() => void run(() => onRemove(detail.group?.card_id ?? '', member.user_id), t('merchant.memberList.groups.removed'))} className="text-red-600">{t('merchant.memberList.groups.remove')}</button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <select aria-label={t('merchant.memberList.groups.assignLabel')} value={assignmentUserId} onChange={event => setAssignmentUserId(event.target.value)} className="min-w-0 flex-1 rounded border px-3 py-2">
                    <option value="">{t('merchant.memberList.groups.assignPlaceholder')}</option>
                    {availableMembers.map(member => <option key={member.userId} value={member.userId}>{member.displayName ?? member.phoneMasked}</option>)}
                  </select>
                  <button type="button" disabled={busy || assignmentUserId.length === 0} onClick={() => void run(() => onAssign(detail.group?.card_id ?? '', assignmentUserId), t('merchant.memberList.groups.assigned'))} className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50">{t('merchant.memberList.groups.assign')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {feedback !== null && <p role="status" className="rounded bg-blue-50 p-2 text-sm text-blue-800">{feedback}</p>}
    </section>
  );
}
